import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Lock, Save, ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const payload = {};
      if (name !== user.name) payload.name = name;
      if (email !== user.email) payload.email = email;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setProfileLoading(false);
        return;
      }

      await axios.put(`${API_URL}/api/profile`, payload, { withCredentials: true });
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update profile';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API_URL}/api/profile/password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#002FA7] mb-8 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <h1 className="text-3xl font-black text-[#0A0A0A] tracking-tight mb-8" data-testid="profile-heading">
          My Profile
        </h1>

        {/* Profile Info */}
        <div className="bg-white border border-[#E5E7EB] p-8 mb-6" data-testid="profile-info-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center rounded-sm">
              <User className="w-5 h-5 text-[#002FA7]" />
            </div>
            <h2 className="text-lg font-bold text-[#0A0A0A]">Personal Information</h2>
          </div>

          <form onSubmit={handleProfileUpdate} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-[#374151]">Full Name</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 rounded-sm border-[#D1D5DB] focus:border-[#002FA7] focus:ring-[#002FA7]"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#374151]">Email Address</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-sm border-[#D1D5DB] focus:border-[#002FA7] focus:ring-[#002FA7]"
                  data-testid="profile-email-input"
                />
              </div>
            </div>

            {/* Read-only account info */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F3F4F6] rounded-sm text-sm text-[#6B7280]">
              <Shield className="w-4 h-4" />
              <span>Role: <span className="font-medium text-[#374151] capitalize">{user?.role}</span></span>
              <span className="mx-2">|</span>
              <span>Subscription: <span className={`font-medium ${user?.subscription_status === 'active' ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                {user?.subscription_status === 'active' ? 'Active' : 'Inactive'}
              </span></span>
            </div>

            <Button
              type="submit"
              disabled={profileLoading}
              className="bg-[#002FA7] hover:bg-[#001B63] text-white font-bold rounded-sm"
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white border border-[#E5E7EB] p-8" data-testid="password-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#DC2626]/10 flex items-center justify-center rounded-sm">
              <Lock className="w-5 h-5 text-[#DC2626]" />
            </div>
            <h2 className="text-lg font-bold text-[#0A0A0A]">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-5">
            <div>
              <Label htmlFor="currentPassword" className="text-sm font-medium text-[#374151]">Current Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10 rounded-sm border-[#D1D5DB]"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="newPassword" className="text-sm font-medium text-[#374151]">New Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 rounded-sm border-[#D1D5DB]"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#374151]">Confirm New Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 rounded-sm border-[#D1D5DB]"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={passwordLoading}
              variant="outline"
              className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/5 font-bold rounded-sm"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
